import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo'
import { certificatesCoursesService } from 'api/certificates'
import { ICertificateCourse } from 'api/certificates/GetAssociatedCourses'
import EmptyState from 'components/reusables/emptyState'
import ProfilesCountCell from 'components/reusables/profilesCountCell'
import RestrictedResourcesModal from 'components/reusables/restrictedResourcesModal'
import { ItemName } from 'components/reusables/restrictedResourcesModal/styled'

interface IProps {
  certificateId: number | string
  close: () => void
  /** Admin templates usan el endpoint de administration */
  admin?: boolean
}

const translationPrefix =
  'administration.certificate.list.associatedCoursesModal'

/**
 * Modal de cursos asociados a un certificado (con alerta de restringidos).
 */
export const CertificateCoursesModal = ({
  certificateId,
  close,
  admin = false,
}: IProps) => {
  const { t } = useTranslation()

  const fetchData = useCallback(
    async ({ page, search, perPage }) => {
      const request = admin
        ? certificatesCoursesService.getAdminAssociatedCourses
        : certificatesCoursesService.getAssociatedCourses

      const response = await request(certificateId, { page, search, perPage })

      return {
        allowedItems: response.data.allowedCourses,
        restrictedCount: response.data.restrictedCoursesCount,
        total: response.data.total,
      }
    },
    [admin, certificateId]
  )

  const customEmpty = useMemo(
    () => (
      <EmptyState title={t(`${translationPrefix}.searchEmpty`)} icon={faBook} />
    ),
    [t]
  )

  return (
    <RestrictedResourcesModal<ICertificateCourse>
      title={t(`${translationPrefix}.title`)}
      close={close}
      fetchData={fetchData}
      columns={[
        {
          title: t(`${translationPrefix}.headers.name`),
          key: 'name',
          render: (_name: unknown, course: ICertificateCourse) => (
            <ItemName $color={course.color}>
              {course.color === 'unset' && (
                <FontAwesomeIcon icon={faCircleInfo} />
              )}
              {course.name}
            </ItemName>
          ),
        },
        {
          title: t(`${translationPrefix}.headers.profiles`),
          key: 'lmsProfiles',
          dataIndex: 'lmsProfiles',
          render: (_lmsProfiles: unknown, course: ICertificateCourse) =>
            course.color === 'unset' ? null : (
              <ProfilesCountCell
                resourceId={course.id}
                resourceType="course"
                count={course.lmsProfiles?.length || 0}
              />
            ),
        },
      ]}
      countIcon={faBook}
      locale={{
        rowsCount: t(`${translationPrefix}.rowsCount`),
        rowsCount_plural: t(`${translationPrefix}.rowsCount_plural`),
        search: t('administration.courses.list.search'),
      }}
      customEmpty={customEmpty}
      closeButtonText={t(`${translationPrefix}.close`)}
      restrictedRowLabel={(count) =>
        t(`${translationPrefix}.restrictedCourse`, { count })
      }
      alertConfig={{
        translationPrefix,
        resourceTypeLabel: t(`${translationPrefix}.resourceType`),
        totalItemKey: 'course',
        restrictedItemKey: 'restrictedCourse',
        totalValueKey: 'totalCourses',
        restrictedValueKey: 'restrictedCourses',
        showAdditionalMessage: false,
      }}
    />
  )
}

export default CertificateCoursesModal
